import { getFunctions, httpsCallable } from "firebase/functions";

import { firebaseApp } from "./firebase";

export type CentralFamilyJourneyStage = "intake" | "personal" | "services";
export type CentralFamilyJourneyNotificationStatus =
  | "accepted"
  | "failed"
  | "not_needed"
  | "not_sent"
  | "pending";

export type CentralFamilyJourneyReadyResponse = {
  duplicate: boolean;
  notificationStatus: CentralFamilyJourneyNotificationStatus;
  notifiedAt?: string | null;
  readyAt: string;
  sent: boolean;
  stage: CentralFamilyJourneyStage;
};

const functions = getFunctions(firebaseApp, "us-east1");

export async function markCentralFamilyJourneyReady(input: {
  caseId: string;
  reviewedSubmissionRevision: number;
  stage: CentralFamilyJourneyStage;
}) {
  const callable = httpsCallable<typeof input, CentralFamilyJourneyReadyResponse>(
    functions,
    "markCentralFamilyJourneyReady",
  );
  return (await callable(input)).data;
}

export async function remindCentralFamilyJourneyStage(input: {
  caseId: string;
  reviewedSubmissionRevision: number;
  stage: Exclude<CentralFamilyJourneyStage, "intake">;
}) {
  const callable = httpsCallable<typeof input, CentralFamilyJourneyReadyResponse>(
    functions,
    "remindCentralFamilyJourneyStage",
  );
  return (await callable(input)).data;
}

export function getCentralFamilyJourneyErrorMessage(error: unknown) {
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
  if (code.includes("failed-precondition")) {
    return "Connect the family arrangement and confirm a valid email address before opening this part.";
  }
  if (code.includes("permission-denied") || code.includes("unauthenticated")) {
    return "Your Central session cannot update the family journey. Sign in again or contact an administrator.";
  }
  if (code.includes("unavailable")) {
    return "This part is ready, but the family email was not accepted. Check the address and retry.";
  }
  return "Central could not update the family journey. Try again.";
}

export function getCentralFamilyJourneyReminderErrorMessage(error: unknown) {
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
  if (code.includes("failed-precondition")) {
    return "Refresh this case before sending another reminder. The step may need staff review or may not be open to the family yet.";
  }
  if (code.includes("permission-denied") || code.includes("unauthenticated")) {
    return "Your Central session cannot send this family reminder. Sign in again or contact an administrator.";
  }
  if (code.includes("unavailable")) {
    return "The reminder email was not accepted. Confirm the family email address and try again.";
  }
  return "Central could not send this family reminder. Try again.";
}
